import { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';

import { Typography, Stack } from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import { deleteCreatorsFromGroup } from '@/api/creatorGroups';
import AutocompleteCreators from '@/components/autocompleteCreators/AutocompleteCreators';
import Button from '@/components/button';
import Dialog from '@/components/dialog/Dialog';
import {
  UpdateCreatorGroups,
  useFetchCreatorGroupsList
} from '@/hooks/queries/creators';
import useAlert from '@/hooks/useAlert';
import { CreatorGroups } from '@/models/creator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const INITIAL_ERRORS = {
  noGroupsSelected: ''
};

const AddCreatorToGroups = ({ isOpen, onClose }: Props) => {
  const id = Number(useParams().id);
  const alert = useAlert();
  const queryClient = useQueryClient();

  const [groups, setGroups] = useState<CreatorGroups.Group[]>([]);
  const [initialSelectedGroups, setInitialSelectedGroups] = useState<
    CreatorGroups.Group[]
  >([]);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [isRemovingGroups, setIsRemovingGroups] = useState<boolean>(false);

  const { data: initialGroups = [] } = useFetchCreatorGroupsList({
    id
  });

  const { groupsToAdd, groupsToRemove } = useMemo(() => {
    const initialIds = initialSelectedGroups.map((group) => group.id);
    const currentIds = groups.map((group) => group.id);

    return {
      groupsToAdd: currentIds.filter((id) => !initialIds.includes(id)),
      groupsToRemove: initialIds.filter((id) => !currentIds.includes(id))
    };
  }, [initialSelectedGroups, groups]);

  useEffect(() => {
    if (initialGroups.length > 0) {
      const currentlySelectedGroups = initialGroups
        .filter((group) => group.selected)
        .map((group) => ({
          ...group,
          name: group.title,
          type: 'Groups' as const
        }));

      setGroups(currentlySelectedGroups);
      setInitialSelectedGroups(currentlySelectedGroups);
    }
  }, [initialGroups]);

  const { mutate: updateCreatorGroups, isPending: isAddingToGroups } =
    UpdateCreatorGroups({
      id,
      onSuccess: () => {},
      onError: () =>
        alert.error(`Some error occurred while adding to the group(s)`)
    });

  const handleSelectGroup = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: CreatorGroups.Group[]
  ) => {
    setGroups(value);
    if (errors.noGroupsSelected !== '') setErrors(INITIAL_ERRORS);
  };

  const invalidateQueries = useCallback(async () => {
    const queryPromises = [
      queryClient.invalidateQueries({
        queryKey: ['creator', id],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creatorGroupsList', id],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creatorGroup'],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creatorGroups'],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creators'],
        refetchType: 'all'
      })
    ];

    await Promise.all(queryPromises);
  }, [queryClient, id]);

  const handleAddGroups = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const allGroupIds = groups.map((group) => group.id);

        updateCreatorGroups(
          { groupIds: allGroupIds },
          {
            onSuccess: () => resolve(),
            onError: () => reject()
          }
        );
      }),
    [updateCreatorGroups, groups]
  );

  const handleRemoveGroups = useCallback(async () => {
    setIsRemovingGroups(true);

    const removePromises = groupsToRemove.map((groupId) =>
      deleteCreatorsFromGroup(groupId, { creators: [id] })
    );

    await Promise.all(removePromises);
    setIsRemovingGroups(false);
  }, [groupsToRemove, id]);

  const getSuccessMessage = useCallback(() => {
    if (groupsToAdd.length > 0 && groupsToRemove.length > 0) {
      return 'Creator groups updated successfully!';
    }
    if (groupsToAdd.length > 0) {
      const groupText = groupsToAdd.length > 1 ? 'groups' : 'group';
      return `Creator successfully added to the ${groupText}!`;
    }
    if (groupsToRemove.length > 0) {
      const groupText = groupsToRemove.length > 1 ? 'groups' : 'group';
      return `Creator successfully removed from the ${groupText}!`;
    }
    return '';
  }, [groupsToAdd.length, groupsToRemove.length]);

  const hasChanges = groupsToAdd.length > 0 || groupsToRemove.length > 0;
  const isSubmitting = isAddingToGroups || isRemovingGroups;

  const handleSubmit = useCallback(async () => {
    if (groups.length === 0) {
      setErrors({
        ...errors,
        noGroupsSelected: 'Please select at least one creator group'
      });
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      await handleRemoveGroups();
      await handleAddGroups();
      await invalidateQueries();

      const successMessage = getSuccessMessage();
      if (successMessage) alert.success(successMessage);

      onClose();
    } catch {
      setIsRemovingGroups(false);
      alert.error('Some error occurred while updating the groups');
    }
  }, [
    groups.length,
    errors,
    hasChanges,
    onClose,
    groupsToAdd.length,
    groupsToRemove.length,
    handleAddGroups,
    handleRemoveGroups,
    invalidateQueries,
    getSuccessMessage,
    alert
  ]);

  const groupsOptions = useMemo(
    () =>
      initialGroups.map((group) => ({
        ...group,
        name: group.title,
        type: 'Groups' as const
      })),
    [initialGroups]
  );

  return (
    <Dialog
      open={isOpen}
      title="Add to group"
      fullWidth
      maxWidth="sm"
      maxHeight={400}
      onClose={onClose}
      dialogContent={
        <Stack marginTop={1}>
          <AutocompleteCreators
            value={groups}
            onChange={handleSelectGroup}
            creatorGroups={groupsOptions}
            label="Creator Groups"
          />
          {errors.noGroupsSelected && (
            <Typography color="error">{errors.noGroupsSelected}</Typography>
          )}
        </Stack>
      }
      actions={
        <>
          <Button variant="text" text="Cancel" onClick={onClose} />
          <Button onClick={handleSubmit} text="Add" loading={isSubmitting} />
        </>
      }
    />
  );
};

export default AddCreatorToGroups;
